import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScanInput } from '@/components/po/ScanInput'
import {
  AdjustmentStatusBadge,
  CountStatusBadge,
} from '@/components/stocktake/StocktakeBadges'
import { StocktakeItemSearch } from '@/components/stocktake/StocktakeItemSearch'
import { StocktakeReview } from '@/components/stocktake/StocktakeReview'
import { StocktakeScanLog } from '@/components/stocktake/StocktakeScanLog'
import { StocktakeTable } from '@/components/stocktake/StocktakeTable'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { useStocktakeScan } from '@/hooks/useStocktakeScan'
import {
  countedOf,
  missedOf,
  scannedOf,
  useChangeStatus,
  useDeleteStocktake,
  useStocktake,
  useStocktakeFilterOptions,
  useUpdateStocktake,
  useUpsertLine,
  type StocktakeLine,
  type StocktakeUpdatePayload,
} from '@/hooks/useStocktakes'
import { ApiError } from '@/lib/api/rawFetch'
import { fmtDateTime, fmtTimeWithSeconds } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { SyncOffBanner } from '@/components/shared/SyncOffBanner'

// The three status moves this screen can make. 'applied' is not one of them —
// it's only reachable through POST /apply.
type StatusAction = 'counting' | 'review' | 'cancelled'

const STATUS_TOASTS: Record<StatusAction, string> = {
  counting: 'Reopened for counting',
  review: 'Count completed — ready to review the adjustment',
  cancelled: 'Stocktake cancelled — Shopify was not touched',
}

// Stocky's four counting tabs.
type CountTab = 'scan' | 'search' | 'log' | 'missed'

// An archived session is a record, but the scan log is still the only place
// that says WHERE something was scanned — months later that's how a KG found in
// an FG box gets traced. So the archive keeps its own two tabs.
type ArchiveTab = 'items' | 'log'

/** Missed rows whose whole baseline is spoken for by unfulfilled orders — the
 *  exchange boxed under the counter that nobody is meant to find on the rack. */
function isFullyHeld(l: StocktakeLine): boolean {
  const onHand = l.baseline_on_hand ?? 0
  return onHand > 0 && (l.baseline_committed ?? 0) >= onHand
}

const selectCls =
  'h-8 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs text-[var(--text-primary)] disabled:opacity-50 disabled:text-[var(--mute)] focus:outline-none focus:border-[var(--brand)]'

/** Order-insensitive: re-ticking the same three racks in another order is not
 *  an edit, and must not fire a PATCH that re-walks the whole vendor. */
function sameTypes(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sortedB = [...b].sort()
  return [...a].sort().every((t, i) => t === sortedB[i])
}

/** Trigger-line summary for the type picker — same wording as the create
 *  dialog, where nothing ticked is the "everything" case. */
function typeSummary(selected: string[]): string {
  if (selected.length === 0) return 'All types'
  const joined = selected.join(', ')
  return joined.length <= 40 ? joined : `${selected.length} types`
}

/** Case 1 of the server's three 409s: dropping product types would delete lines
 *  somebody has already counted, and resending with `discard_counted` is the
 *  way through. The other two 409s are dead ends, so only this one may open the
 *  confirm.
 *
 *  MESSAGE-SHAPE COUPLING: the backend does not flag the retryable case in the
 *  response body, so we read it off the prose. The landed message is "N item(s)
 *  leaving this stocktake have already been counted — those counts would be
 *  discarded. Confirm to remove them anyway"; the two dead-end 409s say
 *  "counted" as well but never "discard", which is why both words are needed. */
function isDiscardable(message: string): boolean {
  const m = message.toLowerCase()
  return m.includes('counted') && m.includes('discard')
}

export default function StocktakeSession() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const id = sessionId ?? ''
  const { data: session, isLoading } = useStocktake(id || null)
  const scanMutation = useStocktakeScan(id)
  const upsertMutation = useUpsertLine(id)
  const statusMutation = useChangeStatus(id)
  const updateMutation = useUpdateStocktake(id)
  const deleteMutation = useDeleteStocktake(id)

  const [tab, setTab] = React.useState<CountTab>('scan')
  const [archiveTab, setArchiveTab] = React.useState<ArchiveTab>('items')
  const [search, setSearch] = React.useState('')
  // Default ON: a variant Shopify already thinks is at 0 is not a shelf anybody
  // failed to walk, and hundreds of them bury the rows that need a second look.
  const [hideZeroMissed, setHideZeroMissed] = React.useState(true)
  // Default OFF: "committed" also covers ordinary unfulfilled online orders,
  // whose stock IS still on the shelf and does still need counting.
  const [hideHeldMissed, setHideHeldMissed] = React.useState(false)
  const [lastError, setLastError] = React.useState<string | null>(null)
  // Unknown/ambiguous barcode = amber attention; failed request = red fault.
  const [lastErrorKind, setLastErrorKind] = React.useState<'not_found' | 'failed'>(
    'not_found'
  )
  const [lastScanTime, setLastScanTime] = React.useState<string | null>(null)
  const [lastScannedLineId, setLastScannedLineId] = React.useState<number | null>(null)
  const [pendingLineId, setPendingLineId] = React.useState<number | null>(null)
  const [cancelOpen, setCancelOpen] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editName, setEditName] = React.useState('')
  const [editNotes, setEditNotes] = React.useState('')
  const [editVendor, setEditVendor] = React.useState('')
  const [editTypes, setEditTypes] = React.useState<string[]>([])
  const [editLocationId, setEditLocationId] = React.useState('')
  // The 409 the operator can answer: the payload we'd resend, plus the server's
  // own wording for why it refused. Null = no confirm pending.
  const [discardPayload, setDiscardPayload] =
    React.useState<StocktakeUpdatePayload | null>(null)
  const [discardMessage, setDiscardMessage] = React.useState('')
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin'

  // Same split as the create dialog: vendors and locations are vendor-agnostic,
  // the product-type list is re-fetched narrowed to whichever vendor is picked.
  const { data: scopeOptions } = useStocktakeFilterOptions()
  const { data: vendorScopeOptions } = useStocktakeFilterOptions(editVendor || undefined)
  const scopeProductTypes = editVendor
    ? (vendorScopeOptions?.product_types ?? [])
    : (scopeOptions?.product_types ?? [])

  // NB: the unmatched-scan sound lives in useStocktakeScan (it already owns the
  // miss/ambiguous branch and its toasts), so this page deliberately does NOT
  // keep its own Audio ref the way Receiving.tsx does — two of them would play
  // the error twice per bad scan.

  const lines = session?.lines ?? []
  const { counted, total } = countedOf(lines)
  const missed = missedOf(lines)
  // Stocky's scan sheet starts EMPTY and grows: rows appear as items are
  // counted, newest scan on top, so the operator's last hit is under their eye.
  const scanned = scannedOf(lines)
  const netAdjustment = lines.reduce((s, l) => s + (l.variance ?? 0), 0)
  const isCounting = session?.status === 'counting'
  const isReview = session?.status === 'review'
  const isApplied = session?.status === 'applied'
  const isArchived = !!session && !isCounting && !isReview

  const handleScan = React.useCallback(
    (barcode: string) => {
      scanMutation.mutate(
        { barcode },
        {
          onSuccess: (result) => {
            if (!result.matched) {
              setLastErrorKind('not_found')
              setLastError(
                result.reason === 'ambiguous'
                  ? 'Barcode is ambiguous in this stocktake'
                  : 'Barcode not found'
              )
            } else {
              setLastError(null)
              setLastScanTime(fmtTimeWithSeconds(new Date()))
              setLastScannedLineId(result.line?.line_id ?? null)
            }
          },
          onError: () => {
            setLastErrorKind('failed')
            setLastError(`Scan failed for "${barcode}"`)
          },
        }
      )
    },
    [scanMutation]
  )

  // Window-level scanner: catches scans even when focus is off the input.
  useBarcodeScanner(handleScan)

  const handleSetCount = React.useCallback(
    (line: StocktakeLine, qty: number) => {
      setPendingLineId(line.line_id)
      upsertMutation.mutate(
        { variant_id: line.variant_id, qty, mode: 'set' },
        {
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to save the count'),
          onSettled: () => setPendingLineId(null),
        }
      )
    },
    [upsertMutation]
  )

  // Scan tab's manual finder: picking an item counts it +1, the same write a
  // scan makes, so the row lands on the scan sheet identically. Increment (not
  // set) because the operator is holding one more of the thing in their hand.
  const handleAddOne = React.useCallback(
    (line: StocktakeLine) => {
      setPendingLineId(line.line_id)
      upsertMutation.mutate(
        { variant_id: line.variant_id, qty: 1, mode: 'increment' },
        {
          onSuccess: () => {
            setLastError(null)
            setLastScanTime(fmtTimeWithSeconds(new Date()))
            setLastScannedLineId(line.line_id)
            toast.success(
              `+1 ${[line.product_title, line.variant_title].filter(Boolean).join(' · ')}`
            )
          },
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to save the count'),
          onSettled: () => setPendingLineId(null),
        }
      )
    },
    [upsertMutation]
  )

  // Missed tab: "we really don't have it". Same absolute write as typing 0 by
  // hand — that's the point, an explicit 0 is what puts the variant into the
  // apply, where never-counted lines are left alone.
  const handleMarkZero = React.useCallback(
    (line: StocktakeLine) => handleSetCount(line, 0),
    [handleSetCount]
  )

  function handleStatus(next: StatusAction) {
    statusMutation.mutate(next, {
      onSuccess: () => {
        setCancelOpen(false)
        toast.success(STATUS_TOASTS[next])
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not change the status'),
    })
  }

  if (isLoading || !session) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[var(--mute)]">
        Loading…
      </div>
    )
  }

  // Name and notes are bookkeeping and stay editable forever. The scope moves
  // too — staff routinely start walking a rack and find the count is missing a
  // neighbouring shelf — but only while the session is open, and only as far as
  // the counts already taken allow.
  function openEdit() {
    setEditName(session!.name)
    setEditNotes(session!.notes ?? '')
    setEditVendor(session!.filter_vendor ?? '')
    // From the canonical list, NOT filter_product_type: that caption is
    // comma-joined and truncated, so splitting it would silently drop a type
    // whose name contains a comma and narrow the scope on save.
    setEditTypes(session!.product_types)
    setEditLocationId(session!.location_id == null ? '' : String(session!.location_id))
    setEditOpen(true)
  }

  function handleEditSave() {
    const name = editName.trim()
    if (!name) return
    const payload: StocktakeUpdatePayload = {}
    if (name !== session!.name) payload.name = name
    // Sent verbatim, empty string included — that is how a note gets cleared.
    if (editNotes !== (session!.notes ?? '')) payload.notes = editNotes
    // An archived session's scope is the record of what was counted, so the
    // fields aren't rendered and nothing is diffed off them.
    if (!isArchived) {
      if (editVendor && editVendor !== (session!.filter_vendor ?? '')) {
        payload.vendor = editVendor
      }
      if (!sameTypes(editTypes, session!.product_types)) {
        payload.product_types = editTypes
      }
      const locationId = Number(editLocationId)
      if (editLocationId && locationId !== session!.location_id) {
        payload.location_id = locationId
      }
    }
    if (Object.keys(payload).length === 0) {
      setEditOpen(false)
      return
    }
    submitEdit(payload)
  }

  function submitEdit(payload: StocktakeUpdatePayload) {
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setEditOpen(false)
        setDiscardPayload(null)
        toast.success('Stocktake updated')
      },
      onError: (err) => {
        const message =
          err instanceof Error ? err.message : 'Could not update the stocktake'
        const status = err instanceof ApiError ? err.status : 0
        // Only offered once, and never taken without an explicit answer: the
        // retry is what throws the counts away.
        if (status === 409 && !payload.discard_counted && isDiscardable(message)) {
          setDiscardMessage(message)
          setDiscardPayload(payload)
          return
        }
        if (status === 503) {
          toast.error(
            'Shopify is not configured / offline mode — the scope cannot be changed. Name and notes can still be saved.'
          )
          return
        }
        toast.error(message)
      },
    })
  }

  function handleDelete() {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setDeleteOpen(false)
        toast.success('Stocktake deleted')
        navigate('/stocktakes')
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not delete the stocktake'),
    })
  }

  const typeLabel = `Barcode / ${session.filter_product_type || 'All'}`
  const searchTerm = search.trim().toLowerCase()
  const searchResults = searchTerm
    ? lines.filter(
        (l) =>
          l.product_title.toLowerCase().includes(searchTerm) ||
          l.sku.toLowerCase().includes(searchTerm)
      )
    : lines

  const COUNT_TABS: { id: CountTab; label: string; count?: number }[] = [
    { id: 'scan', label: 'Scan', count: scanned.length },
    { id: 'search', label: 'Search' },
    { id: 'log', label: 'Log' },
    { id: 'missed', label: 'Missed', count: missed.length },
  ]

  // The Missed filters only hide rows from the sheet — the tab badge above
  // still carries the full count, because "39 missed" is the number the
  // operator has to answer for.
  const zeroHidden = hideZeroMissed
    ? missed.filter((l) => (l.baseline_on_hand ?? 0) === 0).length
    : 0
  const heldHidden = hideHeldMissed ? missed.filter(isFullyHeld).length : 0
  const visibleMissed = missed.filter(
    (l) =>
      !(hideZeroMissed && (l.baseline_on_hand ?? 0) === 0) &&
      !(hideHeldMissed && isFullyHeld(l))
  )

  // Which rows the sheet below shows for the tab in front of the operator.
  const tabLines = !isCounting
    ? lines
    : tab === 'missed'
      ? visibleMissed
      : tab === 'search'
        ? searchResults
        : scanned

  const EMPTY_TAB: Record<CountTab, string> = {
    scan: 'Scan your first item to start counting.',
    search: 'Nothing in this stocktake matches that search.',
    log: '',
    missed:
      missed.length === 0
        ? 'Every variant in this stocktake has been counted.'
        : heldHidden > 0
          ? 'Every missed item here is hidden by the filters — uncheck them to see these items.'
          : 'Every missed item here has 0 expected — uncheck the filter to see them.',
  }

  return (
    <div className="brand-theme flex min-h-screen flex-col bg-[var(--surface-page)] text-[var(--text-primary)]">
      {/* No TopTabs on this page, so the sync-off banner goes here. */}
      <SyncOffBanner />
      {/* Page header */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[14px]">
        <Button
          variant="ghost"
          size="sm"
          className="h-[22px] px-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--text-primary)]"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['stocktakes'] })
            navigate('/stocktakes')
          }}
          data-testid="back-btn"
        >
          ← Back
        </Button>
        <div className="flex flex-col">
          <h1 className="flex items-center gap-2 text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--text-primary)]">
            Stocktake #{session.session_id} · {session.name}
            <CountStatusBadge status={session.status} />
            <AdjustmentStatusBadge status={session.status} />
          </h1>
          <div className="text-[12px] text-[var(--mute)]" data-testid="session-subhead">
            {[
              session.filter_vendor || '—',
              typeLabel,
              session.location_name || 'no location',
              fmtDateTime(session.created_at),
            ]
              .filter(Boolean)
              .join(' · ')}{' '}
            · <span data-testid="progress-summary">{counted} of {total} variants counted</span>
          </div>
        </div>
        <div className="flex-1" />
        {isCounting && (
          <Button
            size="sm"
            className="h-[22px] px-2 text-[11px] font-semibold"
            onClick={() => handleStatus('review')}
            disabled={statusMutation.isPending}
            data-testid="complete-count-btn"
          >
            {statusMutation.isPending ? 'Closing…' : 'Complete Count'}
          </Button>
        )}
        {/* Fixing a typo in the name, or leaving a note for whoever reads the
            count later, stays open in every status — including the archive. */}
        <Button
          variant="ghost"
          size="sm"
          className="h-[22px] px-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--text-primary)]"
          onClick={openEdit}
          data-testid="edit-stocktake-btn"
        >
          Edit
        </Button>
        {/* Throwing away a count is destructive and irreversible, so it's
            admin-only — the server enforces that too. */}
        {isAdmin && (isCounting || isReview) && (
          <Button
            variant="outline"
            size="sm"
            className="h-[22px] border-destructive/50 px-2 text-[11px] text-destructive hover:bg-destructive/5 hover:text-destructive"
            onClick={() => setCancelOpen(true)}
            disabled={statusMutation.isPending}
            data-testid="cancel-count-btn"
          >
            Cancel count
          </Button>
        )}
        {/* Cancel keeps the record; this erases it. Never offered for an
            applied count — that session is the audit trail behind a real
            Shopify adjustment, and the server refuses it with a 409. */}
        {isAdmin && !isApplied && (
          <Button
            variant="outline"
            size="sm"
            className="h-[22px] border-destructive/50 px-2 text-[11px] text-destructive hover:bg-destructive/5 hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
            disabled={deleteMutation.isPending}
            data-testid="delete-stocktake-btn"
          >
            Delete
          </Button>
        )}
      </div>

      {/* Session notes — whatever the person who started the count wrote down */}
      {(session.notes ?? '').trim() && (
        <div className="border-b border-[var(--line-warm)] bg-[var(--surface-page)] px-[18px] py-2 text-[11px] italic text-[var(--ink-2)]">
          {session.notes}
        </div>
      )}

      {isCounting ? (
        <>
          {/* Scan | Search | Log | Missed */}
          <div className="flex h-9 items-stretch border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
            {COUNT_TABS.map((t) => {
              const active = tab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
                    active
                      ? 'text-[var(--text-primary)] font-semibold'
                      : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
                  )}
                  style={active ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' } : undefined}
                  data-testid={`count-tab-${t.id}`}
                >
                  {t.label}
                  {t.count != null && (
                    <span className="text-[10px] text-[var(--mute)]">{t.count}</span>
                  )}
                </button>
              )
            })}
          </div>

          {tab === 'scan' && (
            <div className="flex items-start gap-6 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[16px]">
              <div className="flex-1">
                <ScanInput
                  onScan={handleScan}
                  isPending={scanMutation.isPending}
                  lastError={lastError}
                  lastErrorKind={lastErrorKind}
                  onErrorDismiss={() => setLastError(null)}
                />
                {/* Outside ScanInput's dashed box on purpose: that box refocuses
                    the barcode field on any click inside it, so nesting this
                    would yank the cursor away mid-word. */}
                <div className="mt-2">
                  <StocktakeItemSearch lines={lines} onPick={handleAddOne} />
                </div>
              </div>
              <div className="w-[300px]">
                <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                  Counted
                </div>
                <div className="flex items-baseline gap-1.5">
                  <div className="text-[28px] font-semibold tabular-nums leading-none">
                    {counted}
                  </div>
                  <div className="text-[12px] text-[var(--mute)]">/ {total} variants</div>
                </div>
                <div className="mt-2 flex gap-3.5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                      Net adjustment
                    </div>
                    <div className="text-[12px] font-semibold tabular-nums text-[var(--text-primary)]">
                      {netAdjustment > 0 ? `+${netAdjustment}` : netAdjustment}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                      Last scan
                    </div>
                    <div className="text-[12px] font-semibold tabular-nums text-[var(--text-primary)]">
                      {lastScanTime || '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'search' && (
            <div className="flex items-center gap-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[12px]">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search this stocktake by product name or SKU…"
                className="h-8 max-w-[420px] text-xs"
                data-testid="stocktake-search-input"
              />
              <span className="text-[11px] text-[var(--mute)]">
                Type a Scanned quantity to record a count by hand — for tags that
                won&apos;t scan.
              </span>
            </div>
          )}

          {tab === 'missed' && (
            <div
              className="border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[10px] text-[11px] text-[var(--ink-2)]"
              data-testid="missed-explainer"
            >
              Nobody has counted these yet, so applying this stocktake will{' '}
              <strong>not</strong> touch them — Shopify keeps whatever it has. Use
              Mark as 0 only once you have checked the peg and it really is empty.
              <div className="mt-1.5 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)]">
                  <input
                    type="checkbox"
                    checked={hideZeroMissed}
                    onChange={(e) => setHideZeroMissed(e.target.checked)}
                    data-testid="missed-hide-zero"
                  />
                  Hide items with 0 expected
                </label>
                <label className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)]">
                  <input
                    type="checkbox"
                    checked={hideHeldMissed}
                    onChange={(e) => setHideHeldMissed(e.target.checked)}
                    data-testid="missed-hide-held"
                  />
                  Hide items fully held for orders
                </label>
                {zeroHidden > 0 && (
                  <span className="text-[11px] text-[var(--mute)]" data-testid="missed-zero-hint">
                    Hiding {zeroHidden} zero-stock {zeroHidden === 1 ? 'item' : 'items'}.
                  </span>
                )}
                {heldHidden > 0 && (
                  <span className="text-[11px] text-[var(--mute)]" data-testid="missed-held-hint">
                    Hiding {heldHidden} fully-held {heldHidden === 1 ? 'item' : 'items'}.
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      ) : isReview ? (
        <StocktakeReview
          session={session}
          onBackToCounting={() => handleStatus('counting')}
          statusPending={statusMutation.isPending}
        />
      ) : (
        // Applied / cancelled — the session is now a record of what happened.
        <>
        <div
          className="border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[14px]"
          data-testid="stocktake-archive"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
            {isApplied
              ? `Adjustment performed by ${session.applied_by || 'unknown'}${
                  fmtDateTime(session.applied_at)
                    ? ` on ${fmtDateTime(session.applied_at)}`
                    : ''
                }`
              : 'Cancelled — Shopify was never touched'}
            {session.adjustment_id != null && (
              <Link
                to={`/adjustments/${session.adjustment_id}`}
                className="text-xs font-semibold text-[var(--brand)] underline"
                data-testid="archive-adjustment-link"
              >
                Adjustment #{session.adjustment_id}
              </Link>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--mute)]">
            {counted} of {total} variants counted · net adjustment{' '}
            {netAdjustment > 0 ? `+${netAdjustment}` : netAdjustment} units.{' '}
            {isApplied
              ? `The Applied column shows the delta actually pushed to ${
                  session.location_name || 'the location'
                }.`
              : 'This stocktake was discarded, so no stock was adjusted.'}
          </p>
          {session.apply_error && (
            <p className="mt-1 text-xs text-[var(--status-rejected-fg)]" data-testid="archive-apply-error">
              Last apply failed: {session.apply_error}
            </p>
          )}
        </div>

        {/* Items | Log — the log is the only record of WHERE something was
            scanned, so it has to outlive the count. */}
        <div className="flex h-9 items-stretch border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
          {(['items', 'log'] as ArchiveTab[]).map((t) => {
            const active = archiveTab === t
            return (
              <button
                key={t}
                type="button"
                onClick={() => setArchiveTab(t)}
                className={cn(
                  'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
                  active
                    ? 'text-[var(--text-primary)] font-semibold'
                    : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
                )}
                style={active ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' } : undefined}
                data-testid={`archive-tab-${t}`}
              >
                {t === 'items' ? 'Items' : 'Log'}
              </button>
            )
          })}
        </div>
        </>
      )}

      {/* Count sheet. Skipped in review — StocktakeReview shows the discrepancies
          against LIVE Shopify stock, and two tables of the same rows with
          different deltas is the fastest way to get an adjustment applied wrong. */}
      {isReview ? null : (isCounting && tab === 'log') ||
        (isArchived && archiveTab === 'log') ? (
        <StocktakeScanLog sessionId={id} />
      ) : lines.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 py-16 text-center">
          <p className="text-sm text-[var(--ink-2)]">Nothing to count</p>
          <p className="max-w-md text-xs text-[var(--mute)]">
            This stocktake has no variants — the vendor and product type combination
            matched nothing active in Shopify.
          </p>
        </div>
      ) : tabLines.length === 0 ? (
        <div
          className="flex flex-1 flex-col items-center justify-center py-16 text-center"
          data-testid="stocktake-tab-empty"
        >
          <p className="text-sm text-[var(--ink-2)]">{EMPTY_TAB[tab]}</p>
        </div>
      ) : (
        <StocktakeTable
          lines={tabLines}
          editable={isCounting && tab === 'search'}
          onSetCount={isCounting ? handleSetCount : undefined}
          onMarkZero={isCounting && tab === 'missed' ? handleMarkZero : undefined}
          showApplied={isApplied}
          highlightLineId={lastScannedLineId}
          pendingLineId={pendingLineId}
        />
      )}

      {/* Edit name / notes / scope */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="edit-stocktake-dialog"
        >
          <DialogHeader>
            <DialogTitle>Edit stocktake</DialogTitle>
            <DialogDescription className="text-xs text-[var(--mute)]">
              {isArchived
                ? 'This stocktake is closed — its name and notes can still be corrected.'
                : 'Widening the scope pulls the new items from Shopify with their current on-hand as the Available baseline.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[var(--text-primary)]" htmlFor="edit-stocktake-name">
                Name
              </label>
              <Input
                id="edit-stocktake-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={updateMutation.isPending}
                className="h-8 text-xs"
                data-testid="edit-stocktake-name"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-[var(--text-primary)]" htmlFor="edit-stocktake-notes">
                Notes
              </label>
              <textarea
                id="edit-stocktake-notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                disabled={updateMutation.isPending}
                placeholder="Anything the next person reading this count should know…"
                className="min-h-[56px] w-full border border-[var(--line-input)] bg-transparent px-2 py-1.5 text-xs placeholder:text-[var(--mute)] focus-visible:outline-none focus:border-[var(--brand)] disabled:opacity-50"
                data-testid="edit-stocktake-notes"
              />
            </div>

            <div className="border-t border-[var(--line-warm)] pt-3">
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                Scope
              </div>

              {isArchived ? (
                // The scope of a closed count is not a setting any more, it is
                // the record of what was walked — so it reads as text.
                <div className="mt-1.5 flex flex-col gap-0.5" data-testid="edit-scope-readonly">
                  <div className="text-xs text-[var(--text-primary)]">
                    {session.filter_vendor || '—'} · {session.filter_product_type || 'All types'} ·{' '}
                    {session.location_name || 'no location'}
                  </div>
                  <p className="text-[11px] text-[var(--mute)]">
                    This stocktake is {session.status} — its scope is the record of what
                    was counted and cannot be changed.
                  </p>
                </div>
              ) : (
                <div className="mt-1.5 flex flex-col gap-3">
                  {counted > 0 && (
                    <p
                      className="text-[11px] text-[var(--mute)]"
                      data-testid="edit-scope-counted-note"
                    >
                      {counted} {counted === 1 ? 'item has' : 'items have'} already been
                      counted — changing the vendor or location would invalidate every
                      count. Start a new stocktake instead.
                    </p>
                  )}
                  <div className="flex flex-col gap-1">
                    <label
                      className="text-xs text-[var(--text-primary)]"
                      htmlFor="edit-stocktake-vendor"
                    >
                      Vendor
                    </label>
                    <select
                      id="edit-stocktake-vendor"
                      value={editVendor}
                      onChange={(e) => {
                        setEditVendor(e.target.value)
                        // The old types may not exist under the new vendor.
                        setEditTypes([])
                      }}
                      disabled={updateMutation.isPending || counted > 0}
                      className={selectCls}
                      data-testid="edit-stocktake-vendor"
                    >
                      <option value="">— select a vendor —</option>
                      {(scopeOptions?.vendors ?? []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                      {/* The session's own vendor, even if it no longer sells
                          anything active and so is off the list. */}
                      {session.filter_vendor &&
                        !(scopeOptions?.vendors ?? []).includes(session.filter_vendor) && (
                          <option value={session.filter_vendor}>
                            {session.filter_vendor}
                          </option>
                        )}
                    </select>
                  </div>

                  {/* Same tick list as the create dialog: a count often covers
                      two neighbouring racks, which one dropdown value can't say. */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-[var(--text-primary)]">Product types</label>
                    <div
                      className="border border-[var(--line-warm)] bg-[var(--surface-card)]"
                      data-testid="edit-stocktake-product-types"
                    >
                      <div className="flex items-center justify-between border-b border-[var(--line-warm)] px-2 py-1">
                        <span
                          className="text-xs text-[var(--text-primary)]"
                          data-testid="edit-stocktake-product-types-summary"
                        >
                          {typeSummary(editTypes)}
                        </span>
                        {editTypes.length > 0 && (
                          <button
                            type="button"
                            disabled={updateMutation.isPending}
                            onClick={() => setEditTypes([])}
                            className="text-[11px] text-[var(--mute)] hover:text-[var(--text-primary)]"
                            data-testid="edit-stocktake-product-types-clear"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="max-h-[112px] overflow-auto px-2 py-1">
                        {scopeProductTypes.length === 0 ? (
                          <p className="py-0.5 text-[11px] italic text-[var(--mute)]">
                            No product types to narrow by.
                          </p>
                        ) : (
                          scopeProductTypes.map((t) => (
                            <label
                              key={t}
                              className="flex items-center gap-1.5 py-0.5 text-xs text-[var(--ink-2)]"
                            >
                              <input
                                type="checkbox"
                                disabled={updateMutation.isPending}
                                checked={editTypes.includes(t)}
                                onChange={(e) =>
                                  setEditTypes(
                                    e.target.checked
                                      ? [...editTypes, t]
                                      : editTypes.filter((v) => v !== t)
                                  )
                                }
                                data-testid={`edit-stocktake-product-type-${t}`}
                              />
                              {t}
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label
                      className="text-xs text-[var(--text-primary)]"
                      htmlFor="edit-stocktake-location"
                    >
                      Location
                    </label>
                    <select
                      id="edit-stocktake-location"
                      value={editLocationId}
                      onChange={(e) => setEditLocationId(e.target.value)}
                      disabled={updateMutation.isPending || counted > 0}
                      className={selectCls}
                      data-testid="edit-stocktake-location"
                    >
                      <option value="">— select —</option>
                      {(scopeOptions?.locations ?? []).map((l) => (
                        <option key={l.location_id} value={String(l.location_id)}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <p className="text-[11px] text-[var(--mute)]" data-testid="edit-scope-guardrail">
                    Items still in scope keep their counts and their Available baseline —
                    only the items an edit adds get a fresh reading from Shopify.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleEditSave}
              disabled={updateMutation.isPending || !editName.trim()}
              data-testid="edit-stocktake-save"
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The one 409 the operator can answer: narrowing the scope would delete
          lines somebody has already counted. Never auto-retried — the retry is
          what throws the work away. */}
      <Dialog
        open={discardPayload !== null}
        onOpenChange={(o) => !o && setDiscardPayload(null)}
      >
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="edit-discard-dialog"
        >
          <DialogHeader>
            <DialogTitle>Discard counts on the items you are removing?</DialogTitle>
            <DialogDescription>{discardMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDiscardPayload(null)}
              disabled={updateMutation.isPending}
              data-testid="edit-discard-cancel-btn"
            >
              Keep the scope
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                discardPayload && submitEdit({ ...discardPayload, discard_counted: true })
              }
              disabled={updateMutation.isPending}
              data-testid="edit-discard-confirm-btn"
            >
              {updateMutation.isPending ? 'Saving…' : 'Discard counts & save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="delete-stocktake-dialog"
        >
          <DialogHeader>
            <DialogTitle>Delete this stocktake?</DialogTitle>
            <DialogDescription>
              This permanently removes the count and its scan log. This cannot be undone.
              {session.status === 'cancelled' &&
                ' This stocktake was cancelled, so deleting it erases the record entirely.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Keep it
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              data-testid="delete-stocktake-confirm-btn"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete stocktake'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel count */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="cancel-count-dialog"
        >
          <DialogHeader>
            <DialogTitle>Cancel this stocktake?</DialogTitle>
            <DialogDescription>
              This throws away the count; Shopify is not touched. The stocktake stays on
              record as cancelled and cannot be reopened.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancelOpen(false)}
              disabled={statusMutation.isPending}
            >
              Keep counting
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleStatus('cancelled')}
              disabled={statusMutation.isPending}
              data-testid="cancel-count-confirm-btn"
            >
              {statusMutation.isPending ? 'Cancelling…' : 'Cancel count'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
